import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@playbookai/api-clients";
import { kpiApi } from "../api/live-client.js";
import type { KpiCategoryOption } from "../api/types.js";
import { kpiKeys } from "./kpiQueryKeys.js";

/**
 * PB-2941 — the Create page's Category options: the union of the installed
 * catalog's categories and every code the tenant's configured KPIs already
 * carry (`GET /v1/kpis/category-options`).
 *
 * Assembled COMPLETE in one cached query, by sweeping the endpoint's cursor —
 * the same shape `useOwnerFilterOptions` uses for the distinct-owner set, and
 * for the same reason: the picker searches and sorts the whole set client-side,
 * so a partial page would silently hide a legitimate choice. TanStack owns the
 * cache and de-duplicates concurrent loads, so the sweep runs once per stale
 * window rather than per render. Hitting the page bound throws (surfaced as the
 * field's error state) rather than quietly offering a subset.
 */
const SWEEP_PAGE_LIMIT = 100;
const MAX_SWEEP_PAGES = 10;
const STALE_MS = 5 * 60_000;

export function useKpiCategoryOptions() {
  const config = useApiClient();
  return useQuery<KpiCategoryOption[]>({
    queryKey: kpiKeys.categoryOptions(),
    queryFn: async () => {
      const options: KpiCategoryOption[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < MAX_SWEEP_PAGES; page++) {
        const res = await kpiApi.listCategoryOptions(config, {
          limit: SWEEP_PAGE_LIMIT,
          ...(cursor ? { cursor } : {}),
        });
        options.push(...res.data);
        if (!res.hasMore) return options;
        // `hasMore` with no cursor is a malformed page — surface it rather than
        // returning a set the picker would treat as complete.
        if (!res.nextCursor) {
          throw new Error(
            "KPI category options indicated more results without a next cursor.",
          );
        }
        cursor = res.nextCursor;
      }
      throw new Error(
        `KPI category options exceeded ${MAX_SWEEP_PAGES} pages; refusing to offer a partial category set.`,
      );
    },
    staleTime: STALE_MS,
  });
}
